/**
 * The WebSocket auth ticket, kept usable for the life of an open IDE tab.
 *
 * The app is served from www.molecule.dev and its sockets connect DIRECTLY to
 * api.molecule.dev (Netlify cannot proxy a WS upgrade), so the httpOnly session
 * cookie never reaches them. Every socket instead carries `?token=`, a bearer
 * the API mints from the cookie at `GET /auth/ws-token` — and, being a bearer
 * exposed to JavaScript, it is deliberately SHORT-LIVED (6h). The http client
 * holds it as its auth token, which is where every socket reads it from.
 *
 * Nothing used to renew it. An IDE tab left open across that TTL (a phone put
 * down in the evening and picked up the next morning) reconnected every socket
 * with the dead ticket: each upgrade was rejected as `expired token`, the
 * reconnect budgets burned out, and the tab sat with no live file tree, no
 * chat push, no sandbox status, no LSP and no terminal until a manual refresh
 * minted a fresh one. Plain HTTP kept working (the API swaps a dead presented
 * bearer for the still-valid cookie), which is exactly why nothing looked
 * broken enough to explain — the page loaded, it just never came alive.
 *
 * `ensureWsAuthToken` is the single funnel every socket (re)connect goes
 * through: it re-mints when the held ticket is missing, unreadable-as-expired,
 * or within a minute of expiry, coalesces concurrent callers into one request,
 * and never clobbers a token some other flow set while its mint was in flight
 * (the in-IDE login adopts the new session by re-minting a ticket of its own).
 */

import { getTokenExpiration } from '@molecule/app-auth'

import { silentErrors } from '../bonds/app-http.js'

/** The minimal http surface the ticket funnel needs (a `useHttpClient()` client). */
export interface WsAuthHttp {
  getAuthToken(): string | null
  setAuthToken(token: string | null): void
  get<T>(url: string, config?: { options?: Record<string, unknown> }): Promise<{ data: T }>
}

/**
 * A ticket this close to expiry is re-minted rather than used — an upgrade
 * that lands after the `exp` is rejected, and reconnect backoff can easily
 * spend a minute.
 */
export const WS_TOKEN_MIN_REMAINING_MS = 60_000

/**
 * Whether a held ticket can still open a socket: present, and either not a
 * readable JWT (the server decides) or expiring no sooner than
 * {@link WS_TOKEN_MIN_REMAINING_MS} from now.
 *
 * @param token - The http client's current auth token.
 * @param now - Clock override (tests).
 * @returns `true` when the ticket should be used as-is.
 */
export const wsTokenIsUsable = (token: string | null | undefined, now = Date.now()): boolean => {
  if (!token) return false
  const expiresAt = getTokenExpiration(token)
  if (expiresAt === null) return true
  return expiresAt - now > WS_TOKEN_MIN_REMAINING_MS
}

let inflight: Promise<string | null> | null = null

/**
 * Return a usable WS ticket, minting a fresh one from the session cookie when
 * the held one is missing or (nearly) expired. Resolves to whatever the client
 * holds — possibly `null` — when the mint fails (no session yet, API down);
 * the caller connects with that and lets the server's handshake decide, which
 * is never worse than the previous behaviour.
 *
 * @param http - The app's bonded http client.
 * @returns The ticket to put on the socket URL, or `null`.
 */
export const ensureWsAuthToken = async (http: WsAuthHttp): Promise<string | null> => {
  const held = http.getAuthToken()
  if (wsTokenIsUsable(held)) return held
  if (!inflight) {
    inflight = http
      .get<{ token?: string }>('/auth/ws-token', silentErrors())
      .then((res) => {
        const minted = res.data?.token
        // Another flow (the in-IDE login's adoptLoginSession) may have replaced
        // the token while this mint was in flight — its session is the newer
        // truth, so a ticket minted for the OLD session must not overwrite it.
        if (minted && http.getAuthToken() === held) {
          http.setAuthToken(minted)
          return minted
        }
        return http.getAuthToken()
      })
      .catch(() => http.getAuthToken())
      .finally(() => {
        inflight = null
      })
  }
  return inflight
}
